import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import Swal from "sweetalert2";
import { IoCopyOutline } from "react-icons/io5";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
};

const ApprovePopup = ({
  approvePopupOpen,
  handleCloseApprovePopup,
  withdrawalItem,
  getWithdrawalRequest,
  fetchStats,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const utrRef = useRef(null);
  const [upiCopied, setUpiCopied] = useState(false);
  const [formData, setFormData] = useState({
    requestedAmount: withdrawalItem?.amount || 0,
    status: "Choose",
    tds: 5,
    disbursedAmount: withdrawalItem?.disbursedAmount || 0,
    isManualApprove: false,
    utr: "",
  });

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const staffId = staffData?._id || staffData?.id || "";

  useEffect(() => {
    if (withdrawalItem) {
      setFormData((prev) => ({
        ...prev,
        requestedAmount: withdrawalItem?.amount || 0,
        disbursedAmount:
          prev.disbursedAmount || withdrawalItem?.disbursedAmount || 0,
        tds: prev.tds || 5,
        isManualApprove: prev.isManualApprove,
        utr: prev.utr || "",
      }));
    }
  }, [withdrawalItem]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    const requested = parseFloat(withdrawalItem?.amount) || 0;
    const defaultTds = 5;
    const disbursed = requested - (requested * defaultTds) / 100;

    setFormData((prev) => ({
      ...prev,
      isManualApprove: checked,
      // preserve utr when turning on manual; clear when turning off
      utr: checked ? prev.utr : "",
      status: checked ? "Choose" : prev.status,
      tds: checked ? defaultTds : prev.tds,
      disbursedAmount: checked ? disbursed : prev.disbursedAmount,
    }));
  };

  const handleUtrChange = (e) => {
    setFormData((prev) => ({ ...prev, utr: e.target.value }));
  };

  const handleStatusChange = (status) => {
    const requested = parseFloat(withdrawalItem?.amount) || 0;

    if (status === "Approve") {
      const defaultTds = 5;
      const disbursed = requested - (requested * defaultTds) / 100;
      setFormData((prev) => ({
        ...prev,
        status,
        tds: defaultTds,
        disbursedAmount: disbursed,
        // keep manual approve state and utr if manual mode is on
        isManualApprove: prev.isManualApprove,
        utr: prev.isManualApprove ? prev.utr : "",
      }));
    } else if (status === "Reject") {
      setFormData((prev) => ({
        ...prev,
        status,
        tds: "",
        disbursedAmount: 0,
        isManualApprove: false,
        utr: "",
      }));
    } else if (status === "Choose") {
      const defaultTds = 5;
      const disbursed = requested - (requested * defaultTds) / 100;
      setFormData((prev) => ({
        ...prev,
        status,
        tds: defaultTds,
        disbursedAmount: disbursed,
        isManualApprove: prev.isManualApprove,
        utr: prev.isManualApprove ? prev.utr : "",
      }));
    }

    setDropdownOpen(false);
  };

  const createStaffActivityLog = async (actionType) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description: `Withdrawal ${actionType} for user ${
            withdrawalItem?.name || ""
          } (Amount: ₹${withdrawalItem?.amount})`,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  const handleAction = async (action) => {
    if (action === "manual" && (!formData.utr || formData.utr.trim() === "")) {
      handleClose();
      Swal.fire({
        title: "UTR is mandatory",
        text: "Please enter a valid UTR before manual approval",
        icon: "error",
        confirmButtonColor: "#c51369",
      });

      utrRef.current?.focus();
      return;
    }

    setIsLoading(true);

    try {
      let url = "";
      let payload = {
        userId: withdrawalItem?.userId?._id,
        transactionId: withdrawalItem?._id,
      };

      if (action === "approve") {
        url = `${process.env.REACT_APP_API_URL}/tpi-payout/approve-and-transfer`;
      } else if (action === "reject") {
        url = `${process.env.REACT_APP_API_URL}/tpi-payout/reject-transfer`;
      } else if (action === "manual") {
        url = `${process.env.REACT_APP_API_URL}/tpi-payout/manual/approve-and-transfer`;
        payload = {
          ...payload,
          operatorRef: "manual",
          payid: formData.utr,
        };
      }

      const response = await axios.post(url, payload);

      // 🔥 CALL ACTIVITY LOG HERE
      if (action === "approve") {
        await createStaffActivityLog("Approved Withdrawal");
      } else if (action === "reject") {
        await createStaffActivityLog("Rejected Withdrawal");
      } else if (action === "manual") {
        await createStaffActivityLog("Manually Approved Withdrawal");
      }

      handleClose();

      let successTitle = "";
      let defaultMsg = "";
      let iconType = "success";

      if (action === "approve") {
        successTitle = "Approved";
        defaultMsg = "KYC approved successfully";
      } else if (action === "reject") {
        successTitle = "Rejected";
        defaultMsg = "KYC rejected successfully";
        iconType = "warning";
      } else if (action === "manual") {
        successTitle = "Manual Approved";
        defaultMsg = "Manual transfer approved successfully";
      }

      Swal.fire({
        title: successTitle,
        text: response.data.message || defaultMsg,
        icon: iconType,
        confirmButtonColor: "#c51369",
      });

      await getWithdrawalRequest();
      await fetchStats();
    } catch (error) {
      let defaultErrorMsg = "Something went wrong";
      if (action === "approve") defaultErrorMsg = "Approve failed";
      if (action === "reject") defaultErrorMsg = "Reject request failed";
      if (action === "manual") defaultErrorMsg = "Manual Approve failed";

      handleClose();

      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || error.message || defaultErrorMsg,
        icon: "error",
        confirmButtonColor: "#c51369",
      });

      await getWithdrawalRequest();
      await fetchStats();
    } finally {
      setIsLoading(false);
    }
  };

  //   const handleAction = async (action) => {
  //   if (action === "manual" && (!formData.utr || formData.utr.trim() === "")) {
  //     console.log("❌ Manual Approve failed: No UTR entered");
  //     return;
  //   }

  //   setIsLoading(true);

  //   try {
  //     console.log(`👉 Action triggered: ${action}`);

  //     // simulate API success
  //     await new Promise((res) => setTimeout(res, 500));

  //     handleClose();

  //     console.log(`✅ ${action} success. Now refreshing table and stats...`);

  //     // 🔥 Debug logs instead of real API calls
  //     console.log("📌 getWithdrawalRequest() called");
  //     console.log("📌 fetchStats() called");

  //   } catch (error) {
  //     console.log(`❌ Error during ${action}`, error);

  //     console.log("📌 getWithdrawalRequest() called (after error)");
  //     console.log("📌 fetchStats() called (after error)");

  //     handleClose();
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleClose = () => {
    setFormData({
      requestedAmount: withdrawalItem?.amount || 0,
      status: "Choose",
      tds: 5,
      disbursedAmount: withdrawalItem?.disbursedAmount || 0,
      isManualApprove: false,
      utr: "",
    });
    handleCloseApprovePopup();
  };

  const handleCopyUPI = (upiId) => {
    if (upiId) {
      navigator.clipboard.writeText(upiId);
      setUpiCopied(true);
      setTimeout(() => setUpiCopied(false), 500);
    }
  };

  return (
    <Modal open={approvePopupOpen} onClose={handleClose}>
      <Box sx={style}>
        <div className="relative w-full">
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 text-gray-600 transition-transform duration-200 hover:rotate-90 hover:text-red-500"
          >
            <IoClose size={28} />
          </button>

          <div className="mb-4 flex flex-col items-center">
            <img
              src={withdrawalItem?.userId?.selected_Avatar?.avatarUrl}
              alt="Avatar"
              className="mb-2 h-12 w-12 rounded-full"
            />
            <h2 className="text-xl font-semibold tracking-wide">
              {withdrawalItem?.name || "username not found"}
            </h2>

            <p className="text-sm text-gray-600">
              Verified PAN Name: {withdrawalItem?.name || "-"}
            </p>
            <p className="text-sm text-gray-600">
              Verified UPI Name: {withdrawalItem?.name || "-"}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAction("approve");
            }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="mb-2 block text-lg tracking-wide">
                    Requested Amount
                  </label>
                  <input
                    type="text"
                    value={formData.requestedAmount.toFixed(2)}
                    disabled
                    className="w-full rounded-md border bg-gray-100 p-2 text-gray-800"
                  />
                </div>

                <div className="relative flex-1" ref={dropdownRef}>
                  <label className="mb-2 block text-lg tracking-wide">
                    Status
                  </label>
                  <div
                    className={`relative w-full cursor-pointer rounded-md border p-2 ${
                      formData.status === "Approve"
                        ? "text-green-600"
                        : formData.status === "Reject"
                        ? "text-red-600"
                        : "text-gray-700"
                    }`}
                    onClick={() => setDropdownOpen((prev) => !prev)}
                  >
                    {formData.status}
                    <span className="absolute right-2 top-2">▼</span>
                  </div>
                  {dropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border bg-white shadow">
                      {["Choose", "Approve", "Reject"].map((option) => (
                        <div
                          key={option}
                          className={`cursor-pointer px-4 py-2 hover:bg-gray-100 ${
                            option === "Approve"
                              ? "text-green-600"
                              : option === "Reject"
                              ? "text-red-600"
                              : "text-gray-700"
                          }`}
                          onClick={() => handleStatusChange(option)}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {(formData.status !== "Reject" || formData.isManualApprove) && (
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="mb-2 block text-lg tracking-wide">
                      Charges (%)
                    </label>
                    <input
                      type="number"
                      name="tds"
                      value={formData.tds}
                      readOnly
                      className="w-full rounded-md border bg-gray-100 p-2 text-gray-800"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="mb-2 block text-lg tracking-wide">
                      Disbursed Amount
                    </label>
                    <input
                      type="text"
                      value={formData?.disbursedAmount?.toFixed(2)}
                      disabled
                      className="w-full rounded-md border bg-gray-100 p-2 text-gray-800"
                    />
                  </div>
                </div>
              )}

              {/* UPI ID */}
              {withdrawalItem?.upiId && (
                <div className="relative mt-3">
                  <label className="mb-2 block text-lg tracking-wide">
                    UPI ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={withdrawalItem.upiId}
                      readOnly
                      className="w-full rounded-md border border-gray-400 bg-gray-100 p-2 pr-10 text-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => handleCopyUPI(withdrawalItem.upiId)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-green-600"
                    >
                      <IoCopyOutline size={20} />
                    </button>
                  </div>
                  {upiCopied && (
                    <span className="absolute right-2 top-full mt-1 text-sm text-green-600">
                      Copied!
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="manualApprove"
                  checked={formData.isManualApprove}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4"
                />
                <label
                  htmlFor="manualApprove"
                  className="text-md text-gray-700"
                >
                  Manual Approve
                </label>
              </div>

              {formData.isManualApprove && (
                <div className="mt-3">
                  <label className="mb-2 block text-lg tracking-wide">
                    UTR
                  </label>
                  <input
                    ref={utrRef}
                    type="text"
                    value={formData.utr}
                    onChange={handleUtrChange}
                    className="w-full rounded-md border border-gray-400 p-2 text-gray-800"
                    placeholder="Enter UTR number"
                  />
                </div>
              )}

              {(formData.status !== "Choose" || formData.isManualApprove) && (
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-md bg-gray-300 p-3 font-medium text-gray-800 hover:bg-gray-400"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>

                  {formData.isManualApprove ? (
                    <button
                      type="button"
                      className="rounded-md bg-[#c51369] p-3 font-medium text-white hover:bg-[#a10f5b]"
                      onClick={() => handleAction("manual")}
                      disabled={isLoading}
                    >
                      {isLoading ? <BtnLoader /> : "Manual Approve"}
                    </button>
                  ) : formData.status === "Reject" ? (
                    <button
                      type="button"
                      className="rounded-md bg-red-500 p-3 font-medium text-white hover:bg-red-600"
                      onClick={() => handleAction("reject")}
                      disabled={isLoading}
                    >
                      {isLoading ? <BtnLoader /> : "Reject"}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="rounded-md bg-[#c51369] p-3 font-medium text-white hover:bg-[#a10f5b]"
                      disabled={isLoading}
                    >
                      {isLoading ? <BtnLoader /> : "Approve"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default ApprovePopup;
